/**
 * EAProgressPanel Component
 * Real-time progress display for EA analysis with SignalR updates
 *
 * Features:
 * - Progress bar with percentage
 * - Current stage indicator
 * - Chunk progress (X of Y)
 * - Cancel button
 * - Queue position display
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import QueueIcon from '@mui/icons-material/Queue';
import { getFileTypeConfig } from '../../../api/qbridgeEaApi';
import { ANALYSIS_STATE, PROGRESS_STAGES } from '../../../hooks/useEAAsyncAnalysis';

/**
 * Progress Step Component
 */
const ProgressStep = ({ label, isActive, isComplete }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      opacity: isActive ? 1 : isComplete ? 0.7 : 0.4
    }}
  >
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: isComplete
          ? '#27B56D'
          : isActive
            ? '#7c3aed'
            : 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s ease'
      }}
    >
      {isComplete && (
        <Typography sx={{ color: '#FFFFFF', fontSize: '0.625rem', fontWeight: 700 }}>✓</Typography>
      )}
      {isActive && !isComplete && (
        <CircularProgress size={10} sx={{ color: '#FFFFFF' }} />
      )}
    </Box>
    <Typography
      sx={{
        color: isActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)',
        fontSize: '0.8125rem',
        fontWeight: isActive ? 600 : 400
      }}
    >
      {label}
    </Typography>
  </Box>
);

/**
 * EAProgressPanel Component
 * @param {object} file - The file being analyzed
 * @param {string} state - Current analysis state
 * @param {number} progress - Progress percentage (0-100)
 * @param {string} stage - Current stage name
 * @param {object} stageInfo - Stage label and description
 * @param {number} chunksCompleted - Number of chunks completed
 * @param {number} totalChunks - Total number of chunks
 * @param {number} queuePosition - Position in queue (if queued)
 * @param {function} onCancel - Cancel handler
 */
const EAProgressPanel = ({
  file,
  state,
  progress = 0,
  stage,
  stageInfo,
  chunksCompleted = 0,
  totalChunks = 0,
  queuePosition = 0,
  onCancel
}) => {
  if (!file) return null;

  const fileConfig = getFileTypeConfig(file.file_type);
  const isQueued = state === ANALYSIS_STATE.QUEUED;
  const isProcessing = state === ANALYSIS_STATE.PROCESSING;

  // Define progress steps
  const steps = [
    { key: 'parsing', label: 'Reading Code' },
    { key: 'analyzing', label: 'Analyzing Logic' },
    { key: 'bug_detection', label: 'Bug Detection' },
    { key: 'generating', label: 'Generating Report' }
  ];

  // Determine which steps are complete/active based on progress
  const getStepStatus = (stepKey, index) => {
    const stepProgress = ((index + 1) / steps.length) * 100;
    const isComplete = progress >= stepProgress;
    const isActive = stage === stepKey || (progress >= (index / steps.length) * 100 && progress < stepProgress);
    return { isComplete, isActive };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: { xs: '24px', md: '48px' },
        textAlign: 'center'
      }}
    >
      {/* Animated Icon */}
      <Box
        sx={{
          position: 'relative',
          width: 100,
          height: 100,
          mb: 4
        }}
      >
        {/* Outer ring with progress */}
        <CircularProgress
          variant="determinate"
          value={progress}
          size={100}
          thickness={2}
          sx={{
            color: '#7c3aed',
            position: 'absolute',
            top: 0,
            left: 0,
            '& .MuiCircularProgress-circle': {
              transition: 'stroke-dashoffset 0.5s ease'
            }
          }}
        />

        {/* Background ring */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={100}
          thickness={2}
          sx={{
            color: 'rgba(124, 58, 237, 0.15)',
            position: 'absolute',
            top: 0,
            left: 0
          }}
        />

        {/* Center icon */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isQueued ? (
            <QueueIcon sx={{ color: '#a855f7', fontSize: '1.75rem' }} />
          ) : (
            <AutoAwesomeIcon
              sx={{
                color: '#a855f7',
                fontSize: '1.75rem',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 0.6, transform: 'scale(1)' },
                  '50%': { opacity: 1, transform: 'scale(1.1)' }
                }
              }}
            />
          )}
        </Box>
      </Box>

      {/* Title */}
      <Typography
        sx={{
          color: '#FFFFFF',
          fontSize: '1.25rem',
          fontWeight: 700,
          mb: 1
        }}
      >
        {isQueued ? 'In Queue' : 'Analyzing Your Code'}
      </Typography>

      {/* File Name */}
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.875rem',
          fontFamily: "'JetBrains Mono', monospace",
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Box
          component="span"
          sx={{
            backgroundColor: `${fileConfig.color}20`,
            color: fileConfig.color,
            fontWeight: 700,
            fontSize: '0.625rem',
            padding: '2px 6px',
            borderRadius: '4px'
          }}
        >
          {fileConfig.label}
        </Box>
        {file.name}
      </Typography>

      {/* Queue Position */}
      {isQueued && queuePosition > 0 && (
        <Box
          sx={{
            mb: 3,
            padding: '12px 20px',
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            borderRadius: '10px',
            border: '1px solid rgba(251, 191, 36, 0.2)'
          }}
        >
          <Typography sx={{ color: '#FBBF24', fontSize: '0.875rem', fontWeight: 600 }}>
            Position in queue: {queuePosition}
          </Typography>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem', mt: 0.5 }}>
            Your analysis will start shortly
          </Typography>
        </Box>
      )}

      {/* Progress Bar */}
      {!isQueued && (
        <Box sx={{ width: '100%', maxWidth: '400px', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8125rem' }}>
              {stageInfo?.description || 'Processing...'}
            </Typography>
            <Typography sx={{ color: '#a855f7', fontSize: '0.8125rem', fontWeight: 600 }}>
              {Math.round(progress)}%
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(124, 58, 237, 0.15)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, #7c3aed 0%, #a855f7 100%)',
                transition: 'transform 0.5s ease'
              }
            }}
          />

          {/* Chunk Progress */}
          {totalChunks > 0 && (
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.75rem',
                mt: 1
              }}
            >
              Chunk {chunksCompleted} of {totalChunks}
            </Typography>
          )}
        </Box>
      )}

      {/* Progress Steps */}
      {!isQueued && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: 'flex-start',
            mb: 4
          }}
        >
          {steps.map((step, index) => {
            const { isComplete, isActive } = getStepStatus(step.key, index);
            return (
              <ProgressStep
                key={step.key}
                label={step.label}
                isActive={isActive}
                isComplete={isComplete}
              />
            );
          })}
        </Box>
      )}

      {/* Cancel Button */}
      <Button
        onClick={onCancel}
        startIcon={<CancelIcon />}
        sx={{
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#EF4444',
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 24px',
          borderRadius: '10px',
          '&:hover': {
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            borderColor: 'rgba(239, 68, 68, 0.3)'
          }
        }}
      >
        Cancel Analysis
      </Button>
    </Box>
  );
};

export default EAProgressPanel;
